import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette, Command } from './CommandPalette';
import { getCommandPaletteShortcut } from '@/utils/osDetection';
import NavigationMenu from './Menu';
import { useNavigation } from '@/contexts/NavigationContext';

interface HeaderProps {
  userInfo?: {
    username: string;
    role?: string;
    full_name?: string;
    first_name?: string;
  } | null;
  commands?: Command[];
  isDark?: boolean;
}

/**
 * Dashboard Header with sidebar navigation, breadcrumbs, and back/forward arrows
 * Only used by dashboard pages
 */
const Header: React.FC<HeaderProps> = ({
  userInfo,
  commands,
  isDark = false,
}) => {
  const [showCommandPalette, setShowCommandPalette] = useState<boolean>(false);
  const navigate = useNavigate();
  const { canGoBack, canGoForward, goBack, goForward, breadcrumbs, isDashboardPage } = useNavigation();

  useEffect(() => {
    if (!commands || commands.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commands]);

  if (!isDashboardPage) return null;

  return (
    <>
      <nav className="cgm-bg-primary sticky top-0 z-30 border-b border-[var(--card-border)]" style={{ height: '60px' }}>
        <div className="max-w-[1600px] mx-auto h-full px-2 flex items-center justify-between">
          {/* Left Side - Menu Button */}
          <NavigationMenu 
            userInfo={userInfo} 
            isDark={isDark}
            className="flex-shrink-0"
          />

          {/* Logo */}
          <div className="hidden sm:block mx-2 flex-shrink-0">
            <img
              className="h-12 w-auto"
              src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
              alt="CGM Sports Logo"
              onError={(e) => {
                (e.target as HTMLImageElement).src = isDark
                  ? "/logos/LOGO-MAIN-WHITE.png"
                  : "/logos/LOGO-MAIN-BLK.png";
              }}
            />
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={goBack}
              disabled={!canGoBack}
              className={`cgm-nav-arrow ${canGoBack ? 'cgm-nav-arrow-active' : 'cgm-nav-arrow-disabled'}`}
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goForward}
              disabled={!canGoForward}
              className={`cgm-nav-arrow ${canGoForward ? 'cgm-nav-arrow-active' : 'cgm-nav-arrow-disabled'}`}
              aria-label="Go forward"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="cgm-breadcrumb ml-2 min-w-0 flex-1 overflow-hidden">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && <span className="cgm-breadcrumb-separator"> / </span>}
                  {index === 0 ? (
                    <span className="cgm-breadcrumb-item truncate" style={{ cursor: 'default', opacity: 0.6 }}>
                      {crumb.label}
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate(crumb.path)}
                      className={index === breadcrumbs.length - 1 
                        ? 'cgm-breadcrumb-current truncate' 
                        : 'cgm-breadcrumb-item truncate'}
                    >
                      {crumb.label}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right Side - Quick Actions */}
          {commands && commands.length > 0 && (
            <button
              onClick={() => setShowCommandPalette(true)}
              className="cgm-quick-actions flex-shrink-0 hidden sm:flex"
            >
              <svg className="w-4 h-4 cgm-text-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="cgm-text-secondary text-sm truncate">Quick Actions</span>
              <kbd className="cgm-kbd ml-2 flex-shrink-0">{getCommandPaletteShortcut()}</kbd>
            </button>
          )}
        </div>
      </nav>

      {commands && commands.length > 0 && (
        <CommandPalette
          isOpen={showCommandPalette}
          onClose={() => setShowCommandPalette(false)}
          commands={commands.filter(cmd =>
            !cmd.roles ||
            cmd.roles.length === 0 ||
            (userInfo?.role && cmd.roles.includes(userInfo.role))
          )}
        />
      )}
    </>
  );
};

export default Header;
